import React, { useEffect, useState } from "react";
import { FileSpreadsheet, Download, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "@/components/admin/ui";

export function SendFrsButton() {
  const { user } = useAuth() as any;
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<any>(null);
  const [atOpen, setAtOpen] = useState(false);
  useEffect(() => {
    if (user?.role === "admin") { api.get("/admin/reports/airtable-sync").then((r) => setAt(r.data)).catch(() => {}); }
  }, [user?.role]);
  if (user?.role !== "admin") return null;
  const syncAt = async () => {
    try {
      const { data } = await api.post("/admin/reports/airtable-sync");
      setAt(data);
      const parts = Object.entries(data.added || {}).map(([k, v]) => `${v} ${k}`);
      if (data.errors?.length) toast.error(`Airtable: ${data.errors[0]}`);
      else toast.success(parts.length ? `Added to Airtable: ${parts.join(", ")}` : "No new applicants to add to Airtable");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const total = at?.added ? Object.values(at.added).reduce((s: number, v: any) => s + v, 0) : 0;
  const today = at?.at && new Date(at.at).toDateString() === new Date().toDateString();
  const failed = today && at?.errors?.length;
  const tone = !today ? "border-slate-300 bg-slate-50 text-[#475569]" : failed ? "border-red-200 bg-red-50 text-[#B91C1C]" : "border-emerald-200 bg-emerald-50 text-[#047857]";
  const dot = !today ? "bg-slate-400" : failed ? "bg-[#B91C1C]" : "bg-[#059669]";
  const time = at?.at ? new Date(at.at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
  const label = !today ? "Airtable: no sync yet today" : failed ? `Airtable sync failed ${time}, click to retry` : `Airtable: ${total} added ${time}`;
  const send = async () => {
    try {
      const { data } = await api.post("/admin/reports/daily-intake/send");
      toast.success(`Sheets sent to ${data.to}: ${data.diocese} Diocese, ${data.hyden} Hyden`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const day = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }).replace(/\//g, "-");
  const dl = async (which: "diocese" | "hyden", label: string) => {
    try { await downloadFile(`/admin/reports/daily-intake/download?which=${which}`, `FRS-Query${label}-${day}.xlsx`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <button type="button" onClick={() => setAtOpen(true)} title={failed ? at.errors[0] : "Click to send to Airtable now"} data-testid="airtable-status-badge"
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${tone}`}>
        <span className={`h-2 w-2 rounded-full ${dot}`} />{label}
      </button>
      <button className="btn-secondary btn-sm" onClick={() => dl("diocese", "Diocese")} title="Applications submitted in the last 24 hours" data-testid="download-frs-diocese-button"><Download className="h-4 w-4" /> Diocese Sheet</button>
      <button className="btn-secondary btn-sm" onClick={() => dl("hyden", "Hyden")} title="Applications submitted in the last 24 hours" data-testid="download-frs-hyden-button"><Download className="h-4 w-4" /> Hyden Sheet</button>
      <button className="btn-secondary btn-sm" onClick={() => setOpen(true)} data-testid="send-frs-now-button"><FileSpreadsheet className="h-4 w-4" /> Email sheets now</button>
      <button className="btn-secondary btn-sm" onClick={() => setAtOpen(true)} data-testid="airtable-sync-button"
        title={at?.at ? `Last Airtable sync ${new Date(at.at).toLocaleString()}: ${total} added${at.errors?.length ? " (with errors)" : ""}` : "Not synced yet"}>
        <CloudUpload className="h-4 w-4" /> Send to Airtable{at?.errors?.length ? " (!)" : ""}
      </button>
      <ConfirmDialog open={atOpen} onOpenChange={setAtOpen} testId="airtable-sync-dialog" title="Send to Airtable now?"
        description="Applicants submitted in the last 24 hours that aren't in Airtable yet will be added to the DIOCESE, INFO CLIENT or ATTORNEY table. Applicants already sent are skipped. This also happens automatically every morning."
        confirmLabel="Send to Airtable" onConfirm={syncAt} />
      <ConfirmDialog open={open} onOpenChange={setOpen} testId="send-frs-dialog" title="Email sheets now?"
        description="The Diocese and Hyden spreadsheets for applications submitted in the last 24 hours will be emailed to Wayne right away. The morning email still goes out as usual."
        confirmLabel="Send now" onConfirm={send} />
    </>
  );
}
